"use client";

import { useActionState, useEffect, useState, type JSX, type ReactNode } from "react";
import { toast } from "sonner";
import { useFormStatus } from "react-dom";
import Link from "next/link";
import { Spinner } from "@phosphor-icons/react";
import * as Sentry from "@sentry/nextjs";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import {
  signInWithMagicLinkAction,
  signInWithOAuthAction,
  signInWithPasswordAction,
  type TLoginActionState,
} from "./actions";

export type TLoginClientProps = Readonly<{
  next: string;
  initialEmail?: string;
  adminBypass?: boolean;
  initialErrorCode?: string | null;
}>;

type TLoginMethod = "password" | "magic_link" | "oauth_google" | "oauth_apple";

type TLoginPerfMarker = {
  /** Random ID to correlate the cross-redirect timing payload. */
  id: string;
  /** Unix epoch milliseconds. */
  clickAtMs: number;
  /** Unix epoch milliseconds. */
  submitAtMs: number;
  /** Unix epoch milliseconds (best-effort; captured on pagehide). */
  redirectStartAtMs?: number;
  method: TLoginMethod;
  /** Always path-only. */
  nextPath: string;
};

const LOGIN_PERF_STORAGE_KEY = "tippo.loginPerf.v1";

function nowMs(): number {
  return Date.now();
}

function createPerfMarker(input: {
  method: TLoginMethod;
  nextPath: string;
}): TLoginPerfMarker {
  const t = nowMs();
  return {
    id: crypto.randomUUID(),
    clickAtMs: t,
    submitAtMs: t,
    method: input.method,
    nextPath: input.nextPath,
  };
}

function persistPerfMarker(marker: TLoginPerfMarker): void {
  try {
    sessionStorage.setItem(LOGIN_PERF_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // Best-effort only; never block login.
  }
}

function safeSentryLogContext(marker: TLoginPerfMarker): Record<string, unknown> {
  return {
    loginTraceId: marker.id,
    loginMethod: marker.method,
    nextPath: marker.nextPath,
  };
}

function markLoginStart(method: TLoginMethod, nextPath: string): void {
  const marker = createPerfMarker({ method, nextPath });

  performance.mark("login_click");
  persistPerfMarker(marker);

  Sentry.addBreadcrumb({
    category: "auth",
    message: "login_submit",
    level: "info",
    data: safeSentryLogContext(marker),
  });

  // Capture a best-effort “navigation away from /login” timestamp.
  // This approximates the end of the submit->redirect phase for server-action redirects.
  const onPageHide = () => {
    try {
      const raw = sessionStorage.getItem(LOGIN_PERF_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as TLoginPerfMarker;
      if (!parsed?.id || parsed.id !== marker.id) return;
      const updated: TLoginPerfMarker = {
        ...parsed,
        redirectStartAtMs: nowMs(),
      };
      sessionStorage.setItem(LOGIN_PERF_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // ignore
    }
  };

  window.addEventListener("pagehide", onPageHide, { once: true });
}

function getErrorMessage(errorCode: string): string {
  if (errorCode === "admin_only") {
    return "Admin-only access: please sign in with an admin account.";
  }
  if (errorCode === "auth_callback") {
    return "Sign-in failed. Please try again.";
  }
  return "Something went wrong. Please try again.";
}

/**
 * Renders the submit button label based on the surrounding form's pending state.
 * Uses `useFormStatus` so the button knows about pending without prop-drilling
 * from each action's `useActionState` return.
 */
function SubmitButton({
  className,
  children,
  pendingChildren,
  variant,
  disabled = false,
  ...rest
}: Readonly<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    children: ReactNode;
    pendingChildren: ReactNode;
    variant?: "default" | "outline";
  }
>): JSX.Element {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      variant={variant}
      disabled={pending || disabled}
      className={className}
      {...rest}
    >
      {pending ? pendingChildren : children}
    </Button>
  );
}

export function LoginClient({
  next,
  initialEmail = "",
  adminBypass = false,
  initialErrorCode = null,
}: TLoginClientProps): JSX.Element {
  const initialState: TLoginActionState = initialErrorCode
    ? { status: "error", message: getErrorMessage(initialErrorCode) }
    : null;

  const [passwordState, passwordFormAction] = useActionState(
    signInWithPasswordAction,
    initialState
  );
  const [magicState, magicFormAction] = useActionState(
    signInWithMagicLinkAction,
    null
  );
  const [oauthState, oauthFormAction] = useActionState(
    signInWithOAuthAction,
    null
  );

  // Shared email between the password and magic-link forms.
  const [email, setEmail] = useState(initialEmail);

  const latestErrorMessage =
    passwordState?.status === "error"
      ? passwordState.message
      : magicState?.status === "error"
      ? magicState.message
      : oauthState?.status === "error"
      ? oauthState.message
      : null;
  const magicSent = magicState?.status === "magicSent";

  useEffect(() => {
    if (latestErrorMessage) {
      toast.error(latestErrorMessage);
    }
  }, [latestErrorMessage]);

  const adminBypassValue = adminBypass ? "1" : "";

  return (
    <>
      <h1 className="text-2xl font-semibold text-foreground mb-6">Sign in</h1>

      {magicSent && (
        <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-200">
          Check your email for the magic link.
        </p>
      )}
      {/*
        Email + password.
        Magic-link sits in its own <form> below so each useActionState owns its
        own pending state and the email field can be shared via local state.
      */}
      <form
        action={passwordFormAction}
        className="space-y-4"
        onSubmit={() => markLoginStart("password", next)}
      >
        <input type="hidden" name="next" value={next} />
        <input type="hidden" name="adminBypass" value={adminBypassValue} />
        <Field>
          <FieldLabel htmlFor="login-email">Email</FieldLabel>
          <Input
            id="login-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </Field>
        <Field>
          <div className="flex items-center justify-between">
            <FieldLabel htmlFor="login-password">Password</FieldLabel>
            <Link
              href="/forgot-password"
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="login-password"
            name="password"
            type="password"
            required
            autoComplete="current-password"
          />
        </Field>
        <div className="flex gap-2">
          <SubmitButton
            className="flex-1"
            pendingChildren={
              <>
                <Spinner className="h-4 w-4 animate-spin" aria-hidden />
                Signing in…
              </>
            }
          >
            Sign in
          </SubmitButton>
        </div>
      </form>

      <form
        action={magicFormAction}
        className="mt-2"
        onSubmit={() => markLoginStart("magic_link", next)}
      >
        <input type="hidden" name="next" value={next} />
        <input type="hidden" name="adminBypass" value={adminBypassValue} />
        <input type="hidden" name="email" value={email} />
        <SubmitButton
          variant="outline"
          className="w-full"
          pendingChildren={
            <Spinner className="h-4 w-4 animate-spin" aria-hidden />
          }
        >
          <>
            Magic link
            <PhosphorIcon name="magic-wand" />
          </>
        </SubmitButton>
      </form>

      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-2">Or continue with</p>
        <form action={oauthFormAction} className="flex gap-2">
          <input type="hidden" name="next" value={next} />
          <input type="hidden" name="adminBypass" value={adminBypassValue} />
          <SubmitButton
            name="provider"
            value="google"
            variant="outline"
            className="flex-1"
            onClick={() => markLoginStart("oauth_google", next)}
            pendingChildren={
              <Spinner className="h-4 w-4 animate-spin" aria-hidden />
            }
          >
            Google
          </SubmitButton>
          <SubmitButton
            name="provider"
            value="apple"
            variant="outline"
            className="flex-1"
            onClick={() => markLoginStart("oauth_apple", next)}
            pendingChildren={
              <Spinner className="h-4 w-4 animate-spin" aria-hidden />
            }
          >
            Apple
          </SubmitButton>
        </form>
      </div>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        No account?{" "}
        <Link href="/signup" className="text-foreground font-medium underline">
          Sign up
        </Link>
      </p>
    </>
  );
}
